import { AlertTriangle } from "lucide-react"
import { useRef, useState } from "react"

import { ListaBancos } from "@/componentes/resumen/Conciliacion"
import { FacturacionEnVivo } from "@/componentes/resumen/Facturacion"
import { FichaPartida } from "@/componentes/resumen/FichaPartida"
import { ListaFondos } from "@/componentes/resumen/Fondos"
import { ListaAgrupada, ListaGeneral } from "@/componentes/resumen/Listas"
import { Planificador } from "@/componentes/resumen/Planificador"
import { esOperativa, type Partida, type Resumen, sumaUsd } from "@/componentes/resumen/tipos"
import { monto } from "@/componentes/tesoreria/dinero"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

type Vista =
  | "bancos"
  | "transito"
  | "cxc"
  | "cxp_operativa"
  | "cxp_admin"
  | "gastos_semana"
  | "facturacion"
  | "planificador"
  | "sin_identificar"

const TONO_SEMAFORO: Record<Resumen["cobertura"]["semaforo"], Tono> = {
  VERDE: "exito",
  AMARILLO: "aviso",
  ROJO: "peligro",
}
const COLOR_SEMAFORO = {
  VERDE: "var(--success)",
  AMARILLO: "var(--warning)",
  ROJO: "var(--destructive)",
}

const dinero = (v: string | number) => `$ ${monto(typeof v === "number" ? v.toFixed(2) : v)}`

function Paso({ n, texto }: { n: number; texto: string }) {
  return (
    <h2
      className="text-xs font-semibold uppercase tracking-wide"
      style={{ color: "var(--fg-muted)" }}
    >
      {n} · {texto}
    </h2>
  )
}

/** Una cifra de sólo lectura del panel: rótulo, monto y, si hace falta, de dónde sale. */
function Cifra({ rotulo, valor, nota }: { rotulo: string; valor: string; nota?: string }) {
  return (
    <div className="min-w-0">
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        {rotulo}
      </p>
      <p className="text-lg font-semibold tabular-nums">{valor}</p>
      {nota && (
        <p className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
          {nota}
        </p>
      )}
    </div>
  )
}

/**
 * Resumen Macro: el alcance de la disponibilidad presupuestaria, con el esquema del borrador
 * de Andrea (22/10), arriba lo que sólo se lee: 1 disponibilidad total → 2 reparto 40/60 → 3
 * compromisos vs. cobertura, y la proyección de facturación. Ver `services/resumen_macro.py`.
 *
 * ⚠ **Toda cifra baja al detalle en dos clics** (criterio 2): cada tarjeta abre su lista. El
 * cálculo es del SERVIDOR; aquí sólo se pinta (los subtotales de grupo, en céntimos).
 *
 * ⚠ **Lo que sólo se lee va en UN panel de tres columnas** (30/10, pedido del usuario): eran
 * seis tarjetas y un círculo que ocupaban una pantalla entera antes de llegar a lo que se usa.
 * El semáforo dice su estado con palabra y no sólo con color.
 *
 * ⚠ **Las tarjetas que cambian el detalle van pegadas a él** (30/10, pedido del usuario): arriba,
 * con las cifras fijas de por medio, el clic cambiaba algo que quedaba fuera de la pantalla y no
 * se veía si había pasado algo. Por eso los avisos de arriba, además, llevan hasta el detalle.
 */
export function VistaResumen({
  r,
  planificador,
  soloLectura = false,
}: {
  r: Resumen
  /** El resumen a la tasa BCV: el Planificador no depende de la tasa elegida en pantalla. */
  planificador?: Resumen
  /** La foto del enlace público: sin acciones ni lo que pide sesión. */
  soloLectura?: boolean
}) {
  const [vista, setVista] = useState<Vista>("cxp_operativa")
  const usuario = usarSesion((e) => e.usuario)
  const [abierta, setAbierta] = useState<{ partida: Partida; tipo: "cxc" | "cxp" } | null>(null)
  const operativas = r.cxp.partidas.filter(esOperativa)
  const administrativas = r.cxp.partidas.filter((p) => !esOperativa(p))
  const totalDe = (ps: Partida[]) => sumaUsd(ps.filter((p) => p.suma))
  const bloqueDe = (v: Exclude<Vista, "facturacion" | "planificador">) =>
    r[v === "cxp_operativa" || v === "cxp_admin" ? "cxp" : v]
  // La foto pública no abre nada, y cada ficha pide su permiso: el servidor es quien decide.
  const abreCxc = !soloLectura && tienePermiso(usuario, "cxc:ver")
  const abreCxp = !soloLectura && tienePermiso(usuario, "cxp:ver")
  const abreOrden = !soloLectura && tienePermiso(usuario, "orden_pago:ver")
  const abrirCxp = (partida: Partida) => {
    if (partida.clase === "ORDEN_PAGO" ? abreOrden : abreCxp) setAbierta({ partida, tipo: "cxp" })
  }
  const detalle = useRef<HTMLElement>(null)
  const irAlDetalle = (v: Vista) => {
    setVista(v)
    detalle.current?.scrollIntoView?.({ block: "start" })
  }
  const c = r.cobertura
  const transito = r.transito.partidas.filter((p) => p.clase === "TRANSITO")
  const efectivo = r.transito.partidas.filter((p) => p.clase === "EFECTIVO")
  const reservaPct = Number(r.reserva_pct)

  const entrada = (
    v: Vista,
    rotulo: string,
    valor: string | number,
    nota: string,
    tono?: string,
  ) => (
    <button
      key={rotulo}
      type="button"
      aria-pressed={vista === v}
      onClick={() => setVista(v)}
      className={`tarjeta-interactiva foco-cristal p-4 text-left ${vista === v ? "activo-cristal" : ""}`}
      style={tono ? { borderColor: tono } : undefined}
    >
      <span className="block text-xs uppercase" style={{ color: "var(--fg-muted)" }}>
        {rotulo}
      </span>
      <span className="block text-xl font-semibold tabular-nums">{dinero(valor)}</span>
      <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
        {nota}
      </span>
    </button>
  )

  return (
    <div className="space-y-5">
      {(r.discrepancias > 0 || r.error_2 || r.sin_identificar.partidas.length > 0) && (
        <ul className="space-y-1 text-sm">
          {r.discrepancias > 0 && (
            <li className="flex items-start gap-2" style={{ color: "var(--destructive)" }}>
              <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden={true} />
              <button
                type="button"
                className="text-left underline"
                onClick={() => irAlDetalle("bancos")}
              >
                {r.discrepancias} cuenta(s) con discrepancia registrada sin resolver.
              </button>
            </li>
          )}
          {r.sin_identificar.partidas.length > 0 && (
            <li className="flex items-start gap-2" style={{ color: "var(--warning)" }}>
              <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden={true} />
              <button
                type="button"
                className="text-left underline"
                onClick={() => irAlDetalle("sin_identificar")}
              >
                {r.sin_identificar.partidas.length} ingreso(s) sin pagador identificado (
                {dinero(r.sin_identificar.total)}) no cuentan como disponibles.
              </button>
            </li>
          )}
          {r.error_2 && (
            <li style={{ color: "var(--destructive)" }}>No se pudo leer el 2.0: {r.error_2}</li>
          )}
        </ul>
      )}

      <section
        aria-label="Alcance de la disponibilidad"
        className="tarjeta grid grid-cols-1 divide-y divide-[color:var(--cristal-filo)] p-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.3fr)] lg:divide-x lg:divide-y-0"
      >
        <div className="space-y-1 p-4">
          <Paso n={1} texto="Disponibilidad total" />
          <p className="text-3xl font-semibold tabular-nums">{dinero(c.disponible)}</p>
          <p className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
            Bancos {monto(r.bancos.total)} + tránsito y efectivo{" "}
            {monto(sumaUsd(r.transito.partidas).toFixed(2))} + CxC exigible {monto(r.cxc.total)}
          </p>
        </div>

        <div className="space-y-2 p-4">
          <Paso n={2} texto="Reparto obligatorio" />
          <div
            className="flex h-2 overflow-hidden rounded-full"
            role="img"
            aria-label={`${reservaPct} % reserva, ${100 - reservaPct} % operativo`}
          >
            <div style={{ width: `${reservaPct}%`, background: "var(--warning)" }} />
            <div className="flex-1" style={{ background: "var(--success)" }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Cifra rotulo={`Reserva ${reservaPct} % · no se toca`} valor={dinero(c.reserva)} />
            <Cifra
              rotulo={`Disponible operativo ${100 - reservaPct} %`}
              valor={dinero(c.operativo)}
            />
          </div>
        </div>

        <div className="space-y-2 p-4">
          <Paso n={3} texto="Compromisos vs. cobertura" />
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span
              className="text-xl font-bold tabular-nums"
              style={{ color: COLOR_SEMAFORO[c.semaforo] }}
            >
              {c.ratio === null ? "—" : `${Number(c.ratio).toLocaleString("es-VE")}×`}
            </span>
            <Insignia tono={TONO_SEMAFORO[c.semaforo]}>{c.semaforo}</Insignia>
            <span>{c.motivo}</span>
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Cifra
              rotulo="Compromisos"
              valor={dinero(c.compromisos)}
              nota={`CxP operativas ${dinero(totalDe(operativas))} + administrativo ${dinero(totalDe(administrativas))} + gastos ${dinero(r.gastos_semana.total)}`}
            />
            <Cifra
              rotulo={Number(c.excedente) >= 0 ? "Excedente" : "Faltante"}
              valor={dinero(c.excedente.replace("-", ""))}
              nota="operativo − compromisos"
            />
          </div>
        </div>
      </section>

      <section aria-label="Proyección" className="space-y-2">
        <h2
          className="text-xs font-semibold uppercase tracking-wide"
          style={{ color: "var(--fg-muted)" }}
        >
          Proyección de facturación y de pagos
        </h2>
        <div className="tarjeta grid grid-cols-2 gap-3 p-3 lg:grid-cols-4">
          <Cifra rotulo="Esta semana" valor={dinero(r.cxc.total)} nota="CxC exigible" />
          <Cifra
            rotulo="Semana próxima"
            valor={dinero(r.cxc_valija)}
            nota="en valija, al entregarla (no suma)"
          />
          <Cifra
            rotulo="Total proyectado"
            valor={dinero(Number(r.cxc.total) + Number(r.cxc_valija))}
            nota="esta semana + próxima"
          />
          <Cifra
            rotulo="CxP en borrador"
            valor={dinero(r.proyeccion_cxp)}
            nota="por culminar: no suma"
          />
        </div>
      </section>

      <section aria-label="Entradas y compromisos" className="space-y-2">
        <h2
          className="text-xs font-semibold uppercase tracking-wide"
          style={{ color: "var(--fg-muted)" }}
        >
          Entradas y compromisos · elige una para ver su detalle
        </h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
          {entrada(
            "bancos",
            "Bancos",
            r.bancos.total,
            Number(r.sin_conciliar) > 0
              ? `de ellos sin conciliar ${dinero(r.sin_conciliar)}`
              : "todos conciliados",
          )}
          {entrada(
            "transito",
            "En tránsito",
            sumaUsd(transito),
            transito.length ? transito.map((p) => p.titulo).join(", ") : "nada en camino",
          )}
          {entrada(
            "transito",
            "Efectivo por recibir",
            sumaUsd(efectivo),
            efectivo.length
              ? `se espera ${efectivo.map((p) => (p.fecha ? comoFecha(p.fecha) : "")).join(", ")}`
              : "nada por recibir",
            efectivo.length ? "var(--warning)" : undefined,
          )}
          {entrada("cxc", "CxC exigible", r.cxc.total, "cobranza de esta semana")}
          {entrada(
            "cxp_operativa",
            "CxP operativas",
            totalDe(operativas),
            "por pagar del 2.0 y de expedientes",
            "var(--destructive)",
          )}
          {entrada(
            "cxp_admin",
            "Administrativos por pagar",
            totalDe(administrativas),
            "órdenes y gasto administrativo",
            "var(--destructive)",
          )}
        </div>
      </section>

      <section ref={detalle} aria-label="Detalle" className="scroll-mt-4 space-y-3">
        <div className="flex flex-wrap gap-2 text-sm">
          {(
            [
              ["bancos", "Bancos"],
              ["transito", "Tránsito y por recibir"],
              ["cxc", "Cuentas por cobrar"],
              ["cxp_operativa", "CxP operativas"],
              ["cxp_admin", "Administrativos por pagar"],
              ["gastos_semana", "Gastos de la semana"],
              ...(soloLectura
                ? []
                : ([
                    ["planificador", "Planificador del viernes"],
                    ["facturacion", "Facturación"],
                  ] as const)),
            ] as const
          ).map(([v, t]) => (
            <button
              key={v}
              type="button"
              aria-pressed={vista === v}
              onClick={() => setVista(v)}
              className={`foco-cristal rounded-[var(--radius-control)] px-3 py-1.5 ${vista === v ? "activo-cristal" : "toque-cristal"}`}
            >
              {t}
            </button>
          ))}
        </div>
        {vista !== "facturacion" &&
          vista !== "planificador" &&
          bloqueDe(vista).sin_convertir.length > 0 && (
            <p className="text-xs" style={{ color: "var(--warning)" }}>
              Sin tasa del día, no suman: {bloqueDe(vista).sin_convertir.join(", ")}
            </p>
          )}
        {vista === "bancos" && (
          <ListaBancos
            partidas={r.bancos.partidas}
            estados={r.estados_conciliacion}
            soloLectura={soloLectura}
          />
        )}
        {vista === "transito" && <ListaFondos partidas={r.transito.partidas} />}
        {vista === "cxc" && (
          <ListaAgrupada
            partidas={r.cxc.partidas}
            grupos={r.clases_cxc}
            campo="clase"
            vacia="No hay cuentas por cobrar abiertas."
            onAbrir={abreCxc ? (partida) => setAbierta({ partida, tipo: "cxc" }) : undefined}
          />
        )}
        {vista === "cxp_operativa" && (
          <ListaAgrupada
            partidas={operativas}
            grupos={r.estados_pago}
            campo="estado"
            conOrden={true}
            vacia="No hay cuentas por pagar operativas abiertas."
            onAbrir={abreCxp || abreOrden ? abrirCxp : undefined}
          />
        )}
        {vista === "cxp_admin" && (
          <ListaAgrupada
            partidas={administrativas}
            grupos={r.estados_pago}
            campo="estado"
            conOrden={true}
            vacia="No hay gastos administrativos por pagar."
            onAbrir={abreCxp || abreOrden ? abrirCxp : undefined}
          />
        )}
        {vista === "gastos_semana" && (
          <>
            <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Del {comoFecha(r.semana_desde)} al {comoFecha(r.semana_hasta)}: lo programado que aún
              no tiene orden (la que ya la tiene está en CxP).
              {!r.incluye_nomina && " Sin la nómina: hace falta el permiso de nómina para sumarla."}
            </p>
            <ListaGeneral partidas={r.gastos_semana.partidas} etiqueta="Gastos de la semana" />
          </>
        )}
        {vista === "facturacion" && !soloLectura && <FacturacionEnVivo />}
        {vista === "planificador" && !soloLectura && <Planificador r={planificador ?? r} />}
        {vista === "sin_identificar" && (
          <ListaGeneral partidas={r.sin_identificar.partidas} etiqueta="Ingresos sin identificar" />
        )}
      </section>
      {abierta && (
        <FichaPartida
          key={`${abierta.tipo}-${abierta.partida.clase}-${abierta.partida.ref_id}`}
          partida={abierta.partida}
          tipo={abierta.tipo}
          onCerrar={() => setAbierta(null)}
        />
      )}
    </div>
  )
}
